import type {
  ParameterTimeSeriesThreshold,
  ParameterTimeSeriesThresholdColor,
  ParameterTimeSeriesThresholdType,
} from "@/lib/api";

export type ThresholdDirection = "above" | "below";

export interface ThresholdRowDescriptor {
  id: string;
  color: ParameterTimeSeriesThresholdColor;
  threshold_type: ParameterTimeSeriesThresholdType;
  direction: ThresholdDirection;
}

/**
 * The four pre-enumerated threshold rows, in the fixed vertical order the UI and
 * the persisted array use (top to bottom): red above, orange above, orange
 * below, red below. `above` maps to a `max` threshold (triggers above the value)
 * and `below` to a `min` threshold. Only red/orange are offered.
 */
export const THRESHOLD_ROWS: readonly ThresholdRowDescriptor[] = [
  { id: "red-above", color: "red", threshold_type: "max", direction: "above" },
  { id: "orange-above", color: "orange", threshold_type: "max", direction: "above" },
  { id: "orange-below", color: "orange", threshold_type: "min", direction: "below" },
  { id: "red-below", color: "red", threshold_type: "min", direction: "below" },
];

export interface ThresholdRowState extends ThresholdRowDescriptor {
  active: boolean;
  /** May be `NaN` for an active-but-not-yet-filled row (kept blank in the input). */
  value: number;
}

/**
 * Project the persisted thresholds onto the four canonical rows: a row is active
 * when a threshold with the same `(color, threshold_type)` exists, and takes that
 * threshold's value. Non-canonical entries (other colors, duplicates) are ignored.
 */
export function deriveThresholdRows(
  thresholds: ParameterTimeSeriesThreshold[],
): ThresholdRowState[] {
  return THRESHOLD_ROWS.map((row) => {
    const match = thresholds.find(
      (threshold) =>
        threshold.color === row.color && threshold.threshold_type === row.threshold_type,
    );
    return { ...row, active: !!match, value: match ? match.value : Number.NaN };
  });
}

/**
 * Rebuild the persisted thresholds from row states, always in the canonical order
 * and only for active rows. Values may be `NaN`; those are dropped later by
 * `isValidThreshold` at save/preview.
 */
export function buildThresholdsFromRows(
  rows: ThresholdRowState[],
): ParameterTimeSeriesThreshold[] {
  return THRESHOLD_ROWS.flatMap((descriptor) => {
    const state = rows.find((row) => row.id === descriptor.id);
    if (!state?.active) return [];
    return [
      {
        threshold_type: descriptor.threshold_type,
        color: descriptor.color,
        value: state.value,
      },
    ];
  });
}

export interface ThresholdGaugeBand {
  key: string;
  /** Threshold color for a colored region, or `null` for the neutral (unaffected) band. */
  colorKey: ParameterTimeSeriesThresholdColor | null;
  /** Relative flex weight; proportional to the region's numeric span (tips use a representative weight). */
  weight: number;
}

export interface ThresholdGaugeLine {
  key: string;
  value: number;
  colorKey: ParameterTimeSeriesThresholdColor;
  /** Fraction from the top (0..1) where the line sits, from the cumulative band weights. */
  position: number;
}

/** A dashed reference hint for the value 0, colored by the region it falls in. */
export interface ThresholdGaugeZero {
  /** Fraction from the top (0..1) where 0 sits. */
  position: number;
  /** Region color 0 lands in, or `null` for the neutral (unaffected) band. */
  colorKey: ParameterTimeSeriesThresholdColor | null;
}

export interface ThresholdGaugeSegments {
  /** Visible regions top to bottom: max colors, then the neutral band, then min colors. */
  bands: ThresholdGaugeBand[];
  /** Boundary lines top to bottom, one per active+valid threshold. */
  lines: ThresholdGaugeLine[];
  /**
   * Reference hint for 0, or `null` when there are no active thresholds or a
   * threshold already sits exactly at 0 (its own line already marks it).
   */
  zero: ThresholdGaugeZero | null;
  hasActive: boolean;
}

/** Fraction of the largest finite span used to size each infinite tip. */
const TIP_INFINITE_SCALE = 0.5;

/**
 * Compute the schematic vertical gauge from the row states. Value increases
 * upward: active `max` thresholds stack at the top (each coloring the band from
 * its value up to the next higher one, or +infinity), a neutral band sits in the
 * middle, and active `min` thresholds stack at the bottom (each coloring down to
 * the next lower one, or -infinity).
 *
 * Interior regions are sized by their numeric span, and each infinite tip uses a
 * representative weight of {@link TIP_INFINITE_SCALE} × the largest finite span,
 * so the tips read as smaller than the real regions. Zero-size regions — which
 * happen when two same-direction thresholds share a value — are dropped, so their
 * boundary lines simply coincide. Rows without a finite value are ignored.
 *
 * Value 0 acts as a virtual boundary: when it falls inside one of the infinite
 * tips, the stretch from the outermost threshold to 0 becomes a finite span that
 * sizes proportionally, and only the part beyond 0 keeps the representative
 * (infinite) weight. The 0 hint then sits exactly on that split.
 */
export function computeThresholdGaugeSegments(
  rows: ThresholdRowState[],
): ThresholdGaugeSegments {
  const valid = rows.filter((row) => row.active && Number.isFinite(row.value));
  const maxRows = valid
    .filter((row) => row.threshold_type === "max")
    .sort((a, b) => b.value - a.value);
  const minRows = valid
    .filter((row) => row.threshold_type === "min")
    .sort((a, b) => b.value - a.value);

  const rawBands: { key: string; colorKey: ParameterTimeSeriesThresholdColor | null }[] = [
    ...maxRows.map((row) => ({ key: `band-${row.id}`, colorKey: row.color })),
    { key: "band-neutral", colorKey: null },
    ...minRows.map((row) => ({ key: `band-${row.id}`, colorKey: row.color })),
  ];

  // Lines sit on the boundaries between bands, top to bottom: the max lines
  // (descending) then the min lines (descending), one boundary per threshold.
  const ordered = [...maxRows, ...minRows];
  const lineValues = ordered.map((row) => row.value);
  const bandCount = rawBands.length;

  // Value range of each band ([lower, upper]); the first/last bands are the
  // infinite tips (±Infinity), the rest are finite interior regions.
  const upperOf = (index: number) => (index === 0 ? Infinity : lineValues[index - 1]);
  const lowerOf = (index: number) =>
    index === bandCount - 1 ? -Infinity : lineValues[index];

  // 0 acts as a virtual boundary unless a real threshold already sits there.
  const zeroActive = ordered.length > 0 && !lineValues.some((value) => value === 0);
  const zeroBandIndex = zeroActive
    ? rawBands.findIndex((_, index) => lowerOf(index) < 0 && 0 < upperOf(index))
    : -1;

  // Interior band k (1..bandCount-2) spans the gap between its bounding lines;
  // clamp non-positive spans to 0 so zero/overlapping regions collapse away.
  const interiorSpans = rawBands.map((_, index) => {
    if (index === 0 || index === bandCount - 1) return 0;
    const span = lineValues[index - 1] - lineValues[index];
    return span > 0 ? span : 0;
  });

  // Finite stretch from the outermost threshold to 0 when 0 sits in a tip. This
  // portion of the tip sizes like a real region; the rest stays infinite.
  const isTipBand = (index: number) => index === 0 || index === bandCount - 1;
  const zeroTipSpan =
    zeroBandIndex !== -1 && isTipBand(zeroBandIndex)
      ? Math.max(zeroBandIndex === 0 ? 0 - lowerOf(0) : upperOf(zeroBandIndex) - 0, 0)
      : 0;

  const spanPool = interiorSpans.filter((span) => span > 0);
  if (zeroTipSpan > 0) spanPool.push(zeroTipSpan);
  const largestSpan = spanPool.length > 0 ? Math.max(...spanPool) : 1;
  // The infinite portion of a tip is only half the largest finite span, so tips
  // read as clearly smaller than the real regions rather than matching them.
  const tipInfiniteWeight = largestSpan * TIP_INFINITE_SCALE;

  const weights = rawBands.map((_, index) => {
    // The single band of an empty gauge fills the track; otherwise the first and
    // last bands are the infinite tips (plus the finite 0-stretch when 0 is here).
    if (bandCount === 1) return 1;
    if (isTipBand(index)) {
      return tipInfiniteWeight + (index === zeroBandIndex ? zeroTipSpan : 0);
    }
    return interiorSpans[index];
  });

  const total = weights.reduce((sum, weight) => sum + weight, 0) || 1;
  let cumulative = 0;
  const cumulativeWeights = weights.map((weight) => {
    cumulative += weight;
    return cumulative;
  });

  const lines: ThresholdGaugeLine[] = ordered.map((row, index) => ({
    key: `line-${row.id}`,
    value: row.value,
    colorKey: row.color,
    position: cumulativeWeights[index] / total,
  }));

  const bands: ThresholdGaugeBand[] = rawBands
    .map((band, index) => ({ ...band, weight: weights[index] }))
    .filter((band) => band.weight > 0);

  let zero: ThresholdGaugeZero | null = null;
  if (zeroBandIndex !== -1) {
    const cumulativeBefore = cumulativeWeights[zeroBandIndex] - weights[zeroBandIndex];
    let offsetWithinBand: number;
    if (zeroBandIndex === 0) {
      // Top tip: infinite reserve on top, the finite 0-stretch at the bottom.
      offsetWithinBand = tipInfiniteWeight;
    } else if (zeroBandIndex === bandCount - 1) {
      // Bottom tip: finite 0-stretch on top, infinite reserve below.
      offsetWithinBand = zeroTipSpan;
    } else {
      const upper = upperOf(zeroBandIndex);
      const lower = lowerOf(zeroBandIndex);
      offsetWithinBand = ((upper - 0) / (upper - lower)) * weights[zeroBandIndex];
    }
    const position = (cumulativeBefore + offsetWithinBand) / total;
    zero = {
      position: Math.min(Math.max(position, 0), 1),
      colorKey: rawBands[zeroBandIndex].colorKey,
    };
  }

  return { bands, lines, zero, hasActive: ordered.length > 0 };
}
